import { useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bed,
  Bath,
  Sofa,
  Car,
  Ruler,
  Maximize2,
  Download,
  ExternalLink,
  ArrowLeft,
  Box,
  MessageSquare,
  MapPin,
  ClipboardList,
} from "lucide-react";
import DOMPurify from "dompurify";
import { fetchDesignBySlug } from "../api/public.api.js";
import { assetUrl } from "../api/client.js";
import { BrochureButton } from "../components/designs/DesignCard.jsx";
import { Container } from "../components/layout/Container.jsx";
import { SEO } from "../lib/seo.jsx";
import { Lightbox } from "../components/designs/Lightbox.jsx";
import { InquiryForm } from "../components/forms/InquiryForm.jsx";
import { imageOrPlaceholder, placeholderImage, imgErrorFallback } from "../lib/placeholders.js";

/**
 * Path: /home-designs/:designSlug
 *
 * Per PRD § 8.2 design detail page:
 *  - Image gallery with lightbox
 *  - Full specs table
 *  - Description (rich text)
 *  - Linked facade options
 *  - Linked Matterport tour (if available)
 *  - Inquiry form (formType: design_inquiry, prefilled with design name)
 *  - Brochure download CTA
 */
export default function DesignDetailPage() {
  const { designSlug } = useParams();
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const {
    data: design,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["public", "design", designSlug],
    queryFn: () => fetchDesignBySlug(designSlug),
    staleTime: 60_000,
    retry: false,
  });

  // Photoshoot images (gallery tab) — keep them separate from floor plans
  // now that the data model supports both. heroImage is the canonical
  // "front-of-listing" shot; we deduplicate against the gallery so it
  // doesn't show up twice when admins reuse the same image.
  // Memos must be unconditional (Hooks rules), so we compute them before
  // the early returns and gracefully handle the undefined-design case.
  const fallbackKey = design?.slug ?? design?._id ?? design?.name ?? "design";
  const photoSources = useMemo(() => {
    if (!design) return [];
    const list = [
      ...(design.heroImage ? [design.heroImage] : []),
      ...(design.images ?? []),
    ];
    const seen = new Set();
    const unique = list.filter((src) => {
      if (!src || seen.has(src)) return false;
      seen.add(src);
      return true;
    });
    if (unique.length > 0) return unique.map((src) => assetUrl(src));
    return [
      placeholderImage("design", `${fallbackKey}-1`),
      placeholderImage("interior", `${fallbackKey}-2`),
      placeholderImage("design", `${fallbackKey}-3`),
      placeholderImage("interior", `${fallbackKey}-4`),
    ];
  }, [design, fallbackKey]);

  const floorPlanSources = useMemo(
    () => (design?.floorPlanImages ?? []).map((src) => assetUrl(src)),
    [design]
  );

  if (isError) return <Navigate to="/home-designs" replace />;
  if (isLoading || !design) {
    return (
      <Container className="py-20">
        <div className="h-72 animate-pulse rounded-2xl bg-bg-elevated" />
      </Container>
    );
  }

  const heroImage = photoSources[0];

  // JSON-LD Product schema per PRD § 14.
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: design.name,
    description: design.seo?.description,
    image: heroImage,
    brand: { "@type": "Brand", name: "Shere-e-Punjab Homes" },
  };

  return (
    <>
      <SEO
        title={`${design.name} — Home Designs`}
        description={
          design.seo?.description ??
          `${design.bedrooms} bedroom, ${design.bathrooms} bathroom ${design.storey}-storey design${
            design.lotFits ? `. ${design.lotFits}` : ""
          }.`
        }
        image={heroImage}
        schema={productSchema}
      />

      <Container className="py-10 lg:py-12">
        {/* Breadcrumb */}
        <nav className="mb-6 flex items-center gap-2 text-xs text-text-muted">
          <Link to="/home-designs" className="hover:text-accent">
            Home designs
          </Link>
          <span aria-hidden>/</span>
          <span className="text-text-secondary">{design.name}</span>
        </nav>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px] lg:gap-12">
          <div className="min-w-0 space-y-12">
            {/* Header */}
            <header>
              <Link
                to="/home-designs"
                className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-accent"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to all designs
              </Link>
              <h1 className="mt-3 font-display text-4xl text-text-primary lg:text-6xl">
                {design.name}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-text-secondary">
                <SpecChip icon={Bed} label={`${design.bedrooms} bed`} />
                <SpecChip icon={Bath} label={`${design.bathrooms} bath`} />
                <SpecChip icon={Sofa} label={`${design.livingAreas ?? 1} living`} />
                <SpecChip icon={Car} label={`${design.garage ?? 0} garage`} />
                {design.areaSq && <SpecChip icon={Maximize2} label={`${design.areaSq} sq`} />}
                {design.storey && (
                  <span className="rounded-full border border-border-subtle bg-bg-elevated px-3 py-1 text-xs text-text-secondary">
                    {design.storey} storey
                  </span>
                )}
              </div>
              {design.lotFits && <p className="mt-3 text-sm text-text-muted">{design.lotFits}</p>}

              {/* Inline CTA row — brochure + enquire, mirrors the listing card */}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <BrochureButton design={design} className="!bg-accent !border-accent !text-bg-base hover:!text-bg-base hover:!bg-accent-hover" />
                <a href="#enquire" className="btn-outline">
                  <MessageSquare className="h-4 w-4" />
                  Enquire now
                </a>
              </div>
            </header>

            {/* Combined media gallery — photo-shoot images first, then
                floor plan images side-by-side in the same grid. Each
                floor-plan tile gets a "Floor plan" badge so visitors can
                tell what they're clicking on. */}
            <DesignGallery
              photos={photoSources}
              categories={design.imageCategories ?? {}}
              floorPlans={floorPlanSources}
              floorPlanPdfUrl={design.floorPlanPdf || design.brochurePdf}
              onOpen={(items, i) => setLightboxIdx({ images: items.map((it) => it.src), index: i })}
            />

            {/* Specs grid */}
            <SpecsTable design={design} />

            {/* Site address — Google Maps embed. Renders only when admin
                set a property address on the design. */}
            {design.address && <LocationMap address={design.address} />}

            {/* Description */}
            {design.description && (
              <section>
                <h2 className="font-display text-2xl text-text-primary">About this design</h2>
                <div
                  className="prose-dark mt-4"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(design.description, {
                      USE_PROFILES: { html: true },
                    }),
                  }}
                />
              </section>
            )}

            {/* Linked Matterport */}
            {design.matterport && (
              <section className="rounded-2xl border border-accent/30 bg-accent/5 p-6 lg:p-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="eyebrow">3D virtual tour</p>
                    <h3 className="mt-2 font-display text-2xl text-text-primary">
                      Walk through this design online
                    </h3>
                    <p className="mt-1 text-sm text-text-secondary">
                      A live Matterport scan of the {design.name} display home.
                    </p>
                  </div>
                  <a
                    href={design.matterport.matterportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                  >
                    <Box className="h-4 w-4" /> Open 3D tour
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </section>
            )}

            {/* Linked facades */}
            {design.facades?.length > 0 && (
              <section>
                <h2 className="font-display text-2xl text-text-primary">Available facades</h2>
                <p className="mt-1 text-sm text-text-secondary">
                  Customize the front of your home with one of these facade variants.
                </p>
                <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                  {design.facades.map((f) => (
                    <div
                      key={f._id}
                      className="overflow-hidden rounded-xl border border-border-subtle bg-bg-elevated"
                    >
                      <div className="aspect-[4/3] bg-bg-elevated-2">
                        <img
                          src={imageOrPlaceholder(f.image, {
                            pool: "facade",
                            key: f._id ?? f.title,
                          })}
                          onError={imgErrorFallback(f._id ?? f.title)}
                          alt={f.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                        />
                      </div>
                      <div className="px-3 py-2">
                        <p className="truncate text-sm text-text-primary">{f.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar — sticky inquiry + brochure */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div
              id="enquire"
              className="space-y-4 rounded-2xl border border-border-subtle bg-bg-elevated p-6 scroll-mt-24"
            >
              <div>
                <p className="eyebrow">Talk to us</p>
                <h2 className="mt-2 font-display text-xl text-text-primary">
                  Enquire about {design.name}
                </h2>
                <p className="mt-1 text-xs text-text-muted">
                  {"We'll respond within one business day."}
                </p>
              </div>
              <InquiryForm
                formType="design_inquiry"
                prefill={{
                  designName: design.name,
                  designSlug: design.slug,
                }}
              />
            </div>

            {/* Always-visible brochure CTA — full-width download button */}
            <div className="mt-4 rounded-xl border border-border-subtle bg-bg-elevated p-4">
              <div className="mb-3 flex items-start gap-3">
                <Download className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                <div>
                  <p className="text-sm font-medium text-text-primary">Brochure</p>
                  <p className="text-xs text-text-muted">PDF · floor plans + specs</p>
                </div>
              </div>
              <BrochureButton design={design} className="w-full" />
            </div>
          </aside>
        </div>
      </Container>

      {lightboxIdx && (
        <Lightbox
          images={lightboxIdx.images}
          index={lightboxIdx.index}
          onChange={(i) => setLightboxIdx((s) => (s ? { ...s, index: i } : s))}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  );
}

/**
 * Combined gallery — photoshoot images first, then floor-plan drawings,
 * all in the same grid. Floor-plan tiles get a "Floor plan" badge so the
 * visitor knows they're looking at architectural plans rather than a
 * photo. A "Download plans (PDF)" button sits above the grid when the
 * admin attached the PDF.
 *
 * `onOpen` receives the FULL combined items array + the clicked index, so
 * the lightbox shows photos and plans in the same flow.
 */
function DesignGallery({ photos = [], categories = {}, floorPlans = [], floorPlanPdfUrl, onOpen }) {
  const [activeTab, setActiveTab] = useState("all");

  const items = [
    ...photos.map((src) => ({ src, kind: "photo" })),
    ...floorPlans.map((src) => ({ src, kind: "floor-plan" })),
  ];
  if (items.length === 0) return null;

  const featuredImage = photos[0];

  // Define our display categories
  const GALLERY_CATEGORIES = {
    all: "All Photos",
    kitchen: "Kitchen",
    bathroom: "Bathroom",
    bedroom: "Bedroom",
    pool: "Pool & Outdoor",
    living: "Living Areas",
    general: "Interior & General",
    "floor-plans": "Floor Plans",
  };

  // Helper to determine category with dynamic filename fallback
  const getCategory = (src) => {
    if (categories) {
      if (categories[src]) return categories[src];
      // Match relative path key as a suffix of the absolute image URL
      const matchedKey = Object.keys(categories).find((key) => key && src.includes(key));
      if (matchedKey) return categories[matchedKey];
    }
    const name = src.toLowerCase();
    if (name.includes("kitchen") || name.includes("dining") || name.includes("pantry") || name.includes("tambour") || name.includes("island") || name.includes("butler")) return "kitchen";
    if (name.includes("bath") || name.includes("ensuite") || name.includes("powder") || name.includes("toilet") || name.includes("vanity") || name.includes("wc") || name.includes("shower")) return "bathroom";
    if (name.includes("bed") || name.includes("bedroom") || name.includes("sleep") || name.includes("master") || name.includes("guest") || name.includes("robe") || name.includes("wardrobe")) return "bedroom";
    if (name.includes("pool") || name.includes("spa") || name.includes("outdoor") || name.includes("alfresco") || name.includes("patio") || name.includes("deck") || name.includes("garden") || name.includes("backyard") || name.includes("facade") || name.includes("exterior") || name.includes("front") || name.includes("landscap")) return "pool";
    if (name.includes("living") || name.includes("lounge") || name.includes("family") || name.includes("rumpus") || name.includes("theatre") || name.includes("sofa") || name.includes("fireplace") || name.includes("entry") || name.includes("study") || name.includes("hall")) return "living";
    return "general";
  };

  // Group photos according to categories saved in the database or fallback filename categories
  const grouped = {
    all: photos,
    kitchen: photos.filter((src) => getCategory(src) === "kitchen"),
    bathroom: photos.filter((src) => getCategory(src) === "bathroom"),
    bedroom: photos.filter((src) => getCategory(src) === "bedroom"),
    pool: photos.filter((src) => getCategory(src) === "pool"),
    living: photos.filter((src) => getCategory(src) === "living"),
    general: photos.filter((src) => getCategory(src) === "general"),
    "floor-plans": floorPlans,
  };

  // Filter tabs: only show tabs that have at least one image
  const activeTabs = Object.keys(GALLERY_CATEGORIES).filter((key) => {
    if (key === "all") return photos.length > 0;
    return grouped[key] && grouped[key].length > 0;
  });

  // Ensure selected tab is valid (fallback to first available tab)
  const currentTab = activeTabs.includes(activeTab) ? activeTab : (activeTabs[0] ?? "all");
  const currentImages = grouped[currentTab] ?? [];

  // Map the current images to items with their proper kinds for the lightbox
  const tabItems = currentImages.map((src) => ({
    src,
    kind: currentTab === "floor-plans" ? "floor-plan" : "photo",
  }));

  // Handle clicking the featured hero image (opens full photos series starting at index 0)
  const handleHeroClick = () => {
    const photoItems = photos.map((src) => ({ src, kind: "photo" }));
    onOpen(photoItems, 0);
  };

  return (
    <section className="space-y-8">
      {/* 1. Widescreen Featured Image (Hero Image) */}
      {featuredImage && (
        <div
          onClick={handleHeroClick}
          className="group relative w-full aspect-[16/9] sm:aspect-[21/9] lg:aspect-[16/7] overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated cursor-pointer shadow-lg transition-all duration-300 hover:shadow-accent/10"
        >
          <img
            src={featuredImage}
            alt="Featured Design Hero"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            loading="eager"
          />
          {/* Subtle Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-90 transition-opacity group-hover:opacity-95" />
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between md:bottom-6 md:left-6 md:right-6">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-bg-base shadow-sm backdrop-blur-sm">
              View Gallery
            </span>
            <span className="text-xs font-medium text-white/80 drop-shadow">
              Click to view photoshoot ({photos.length} photos)
            </span>
          </div>
        </div>
      )}

      {/* 2. Gallery Tabs Bar & PDF Download */}
      <div>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Gallery</p>
            <h2 className="mt-1 font-display text-2xl text-text-primary">
              Browse Design Spaces
            </h2>
          </div>
          {floorPlanPdfUrl && (
            <a
              href={assetUrl(floorPlanPdfUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-border-subtle bg-bg-elevated px-4 py-2 text-xs font-semibold text-text-secondary transition-all hover:border-accent hover:text-accent"
            >
              <Download className="h-3.5 w-3.5" /> Download plans (PDF)
            </a>
          )}
        </div>

        {/* Tab Buttons */}
        <div className="border-b border-border-subtle mb-6">
          <div className="flex flex-wrap gap-2 md:gap-4 -mb-px overflow-x-auto pb-1 scrollbar-none">
            {activeTabs.map((tabKey) => {
              const isActive = tabKey === currentTab;
              const count = grouped[tabKey]?.length ?? 0;
              return (
                <button
                  key={tabKey}
                  type="button"
                  onClick={() => setActiveTab(tabKey)}
                  className={`pb-3 px-1 text-sm font-semibold border-b-2 transition-all duration-300 whitespace-nowrap flex items-center gap-2 ${
                    isActive
                      ? "border-accent text-accent"
                      : "border-transparent text-text-muted hover:text-text-secondary"
                  }`}
                >
                  {GALLERY_CATEGORIES[tabKey]}
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      isActive ? "bg-accent/15 text-accent" : "bg-bg-elevated text-text-muted"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 3. Responsive Gallery Grid for Active Tab */}
        {tabItems.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 lg:gap-4">
            {tabItems.map((item, idx) => (
              <GalleryTile
                key={item.src}
                item={item}
                onClick={() => onOpen(tabItems, idx)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border-subtle p-8 text-center text-sm text-text-muted bg-bg-elevated/40">
            No images in this category yet.
          </div>
        )}
      </div>
    </section>
  );
}

function GalleryTile({ item, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative overflow-hidden bg-bg-elevated rounded-xl aspect-square w-full border border-border-subtle"
    >
      <img
        src={item.src}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
      />
      {item.kind === "floor-plan" ? (
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-bg-base/85 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-accent backdrop-blur">
          <ClipboardList className="h-3 w-3" /> Floor plan
        </span>
      ) : (
        <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <span className="rounded-full bg-bg-base/90 p-2 text-text-primary shadow backdrop-blur scale-90 group-hover:scale-100 transition-transform">
            <Maximize2 className="h-4 w-4" />
          </span>
        </div>
      )}
    </button>
  );
}

/**
 * Property location — Google Maps embed using the address string. Doesn't
 * need an API key for the basic `/maps?q=` embed and works on every site.
 *
 * Layout: full-width 16:9 map up top with a generous min-height so it
 * never collapses on slow networks, plus a labelled address card and
 * "Open in Google Maps" button below — covers the case where the iframe
 * is blocked (ad-blockers, restrictive networks) by always showing the
 * address + outbound link.
 */
function LocationMap({ address }) {
  const encoded = encodeURIComponent(address);
  const embedSrc = `https://www.google.com/maps?q=${encoded}&output=embed`;
  const linkHref = `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Site location</p>
          <h2 className="mt-2 font-display text-2xl text-text-primary">{address}</h2>
          <p className="mt-1 text-xs text-text-muted">
            The site these working drawings were prepared for.
          </p>
        </div>
        <a
          href={linkHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-md border border-accent bg-accent px-4 py-2 text-xs font-medium text-bg-base transition-colors hover:bg-accent-hover"
        >
          <MapPin className="h-3.5 w-3.5" /> Open in Google Maps
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="mt-4 overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated">
        <div className="relative aspect-[16/10] w-full min-h-[320px] lg:min-h-[420px]">
          <iframe
            title={`Map of ${address}`}
            src={embedSrc}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="absolute inset-0 h-full w-full border-0"
            allowFullScreen
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 border-t border-border-subtle bg-bg-elevated-2/60 px-4 py-3 text-xs text-text-secondary">
          <MapPin className="h-3.5 w-3.5 text-accent" />
          <span className="text-text-primary">{address}</span>
          <a
            href={linkHref}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-accent hover:text-accent-hover"
          >
            Get directions <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </section>
  );
}

function SpecChip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-elevated px-3 py-1 text-xs">
      <Icon className="h-3.5 w-3.5 text-accent" />
      {label}
    </span>
  );
}

function SpecsTable({ design }) {
  const rows = [
    { label: "Storey", value: design.storey, icon: Maximize2 },
    { label: "Bedrooms", value: design.bedrooms, icon: Bed },
    { label: "Bathrooms", value: design.bathrooms, icon: Bath },
    { label: "Living areas", value: design.livingAreas, icon: Sofa },
    { label: "Garage", value: design.garage, icon: Car },
    { label: "Area", value: design.areaSqm ? `${design.areaSqm} m² (${design.areaSq} sq)` : "—" },
    {
      label: "Length × Width",
      value: design.length && design.width ? `${design.length}m × ${design.width}m` : "—",
    },
    { label: "Block fit", value: design.lotFits || "—", icon: Ruler },
  ];

  return (
    <section>
      <h2 className="font-display text-2xl text-text-primary">Specifications</h2>
      <dl className="mt-4 grid grid-cols-2 gap-4 rounded-2xl border border-border-subtle bg-bg-elevated p-6 sm:grid-cols-4">
        {rows.map((r) => (
          <div key={r.label}>
            <dt className="text-xs uppercase tracking-wider text-text-muted">{r.label}</dt>
            <dd className="mt-1 text-sm text-text-primary">{r.value ?? "—"}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}